import {Render} from "./Render";
import {AzureBaseMap} from "../AzureBaseMap";
import TerritoryModel from "../../TerritoryModel";
import {TerritoryLayerCollection} from "../../layer/territory/TerritoryLayerCollection";
import {AzureSelect} from "../AzureSelect";
import $ from "jquery";
import {ILayerView, LayerInfo} from "@esWebAPI/es/model/Layer";
import {IEventChannel, InitMapParams} from "@esWebAPI/es/model/Models";
import {FeatureViewModel} from "@esWebAPI/es/map/FeatureViewModel";
import {TerritoryPointMapping} from "@esWebAPI/es/map/TerritoryPointMapping";
import {AzureOverlay} from "@esWebAPI/es/map/module/AzureOverlay";
import {LeaderLineLabeler} from "@esWebAPI/es/map/module/LeaderLineLabeler";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {PointLayer} from "@esWebAPI/es/map/layer/PointLayer";
import {TerritoryBaseLayer} from "@esWebAPI/es/map/layer/territory/TerritoryBaseLayer";
import {TerritoryPolygonRelatedLayer} from "@esWebAPI/es/map/layer/territory/TerritoryPolygonRelatedLayer";
import {TerritoryPointRelatedLayer} from "@esWebAPI/es/map/layer/territory/TerritoryPointRelatedLayer";
import {TerritoryPointLayer} from "@esWebAPI/es/map/layer/territory/TerritoryPointLayer";

export class TerritoryRender extends Render {

    private readonly _territoryModel: TerritoryModel;
    private _territoryLayerCollection: TerritoryLayerCollection|undefined;
    private isDoNotRenderTerritoryLayers = false;
    private _territoryRenderLoadComplete = $.Deferred();


    constructor(
        baseMap: AzureBaseMap,
        featureViewModel: FeatureViewModel,
        _selectModule: AzureSelect,
        territoryModel: TerritoryModel,
        territoryLayerCollection: TerritoryLayerCollection,
        initMapParams: InitMapParams,
        overlayModule: AzureOverlay,
        stylerFactory: ThematicStylerFactory,
        eventChannel: IEventChannel
    ) {

        super(baseMap, featureViewModel, undefined, initMapParams, overlayModule, stylerFactory, eventChannel);
        this._territoryModel = territoryModel;
        this.isDoNotRenderTerritoryLayers = initMapParams?.isDoNotRenderTerritoryLayers === true;

        this.eventChannel.addListener('onTerritoryLayerCollectionLoadFinish', super.reorderLayers, this);
        this.eventChannel.addListener("onTerritoryLayerLoadFinish", super.reorderLayers, this);

        this._loadComplete.then(() => {
            this._territoryLayerCollection = territoryLayerCollection;
            Promise.all([
                this._territoryLayerCollection.init(),
                this.initPlusMinusSymbols()
            ]).then(() => {
                this._territoryRenderLoadComplete.resolve();
            });
        })
    }

    protected onAddLayer(layerView: ILayerView) {
        if (TerritoryPointMapping.isLayerViewSupported(this._territoryModel, layerView)){
            this.eventChannel.addOnce("onTerritoryPointMappingUpdate", () => {
                super.onAddLayer(layerView);
            });
        } else {
            super.onAddLayer(layerView);
        }
    }

    protected async load(): Promise<void> {
        /* Make sure territoryModel and relatedLayerViewMapping are loaded before creating layers otherwise layers
        will be created as AzurePolygonLayer instead of AzureTerritoryPolygonLayer. */
        await this._territoryModel.loadComplete;

        await Promise.all(this.featureViewModel.listLayers().map((lvm: any) => {
            // isDoNotRenderTerritoryLayers will be true for isStyleOrLocationsPage as we do not want to render the ter layers there
            if (this.isDoNotRenderTerritoryLayers && lvm.getType().startsWith("ManageTerritory")) {
                es.log.log("isDoNotRenderTerritoryLayers : skip layer", lvm.getType(), lvm.getTitle());
                return Promise.resolve();
            }
            const layerConfig = this.initMapParams?.layers?.find(lvConfig => lvConfig.id === lvm.getId());
            if (layerConfig?.enabled === false) {
                // Skip this layer if explicitly disabled
                return;
            }

            return this.addLayer(lvm.getLayerView());
        }));

        const dfds: JQuery.Deferred<void>[] = [];
        for (const layer of this.baseMap.layers) {
            dfds.push(layer.onReady)
        }

        await Promise.all(dfds).then(() => {

            const llLabeler = new LeaderLineLabeler(this.baseMap, this.featureViewModel, this.overlayModule, this.eventChannel);
            llLabeler.init().then(() => {
                this.reorderLayers();
                this.baseMap.map.events.add('layeradded', (layer) => {
                    if (this.baseMap.getLayerById(layer.getId())) {
                        es.log.log("layeradded", layer);
                        this.reorderLayers();
                    }
                });
                this._loadComplete.resolve();
            });
        });
    }


    protected async reload(): Promise<void> {
        await this._territoryModel.pointMapping.reset();
        await super.reload();
        this._territoryLayerCollection?.getTerritoryOutlinesLayer()?.destroy();
        this._territoryLayerCollection?.getTerritoryLabelLayer()?.destroy();
        await this._territoryLayerCollection?.init();
    }

    private initPlusMinusSymbols(): JQuery.Promise<void> {
        const plusStyle = {
            $type: "SymbolStyle",
            color: 4278190080,
            symbolTypeId: 17,
            size: 20
        };
        const minusStyle = {...plusStyle, symbolTypeId: 55};
        return $.when(this.spriteManager.addFromStyle(plusStyle), this.spriteManager.addFromStyle(minusStyle));
    }

    get loadComplete(): JQuery.Deferred<any, any, any> {
        return this._territoryRenderLoadComplete;
    }

    protected async addSymbolLayer(layerView: ILayerView, layerInfo: any) {
        if (layerView.$type === "MapScopeCustomPointLayerView" || layerView.$type === "MapScopeTerritoryCentersLayerView") {
            this.baseMap.addLayer(new PointLayer(this._map, layerView, layerInfo, this.baseMap.getDatasourceManager(),
                this.spriteManager, this.stylerFactory, this.eventChannel));
            return;
        }
        if (this._territoryModel.isRelatedExtraPointLayer(layerView.id)) {
            const territoryPointRelatedLayer = new TerritoryPointRelatedLayer(this._map, layerView, layerInfo, this.baseMap.getDatasourceManager(),
                this.spriteManager, this.stylerFactory, this._territoryModel, this.eventChannel);
            await territoryPointRelatedLayer.onReady;
            this.baseMap.addLayer(territoryPointRelatedLayer);
            return;
        }
        this.baseMap.addLayer(new TerritoryPointLayer(this._map, layerView, layerInfo, this.baseMap.getDatasourceManager(),
            this.spriteManager, this.stylerFactory, this._territoryModel, this.eventChannel));
    }

    protected addPolygonLayer(layerView: ILayerView, layerInfo: LayerInfo) {

        if (this.initMapParams.isTerritoryComparisonMasterMap){
            if (layerView.$type === "ManageTerritoryMainLayerView" && !this._territoryModel.isAccountOnly()) {
                super.addPolygonLayer(layerView, layerInfo);
            }
            return;
        }

        if (layerView.$type === "ManageTerritoryMainLayerView") {
            return;
        }
        if(this._territoryModel.isBaseLayer(layerView.id)) {
            this.baseMap.addLayer(new TerritoryBaseLayer(this._map, layerView, layerInfo,
                this.baseMap.getDatasourceManager(), this.spriteManager, this.stylerFactory,
                this._territoryModel, this.eventChannel));

        } else if(this._territoryModel.isRelatedLayer(layerView.id)){
            this.baseMap.addLayer(new TerritoryPolygonRelatedLayer(this._map, layerView, layerInfo,
                this.baseMap.getDatasourceManager(), this.stylerFactory, this._territoryModel, this.eventChannel));
        } else {
            super.addPolygonLayer(layerView, layerInfo);
        }
    }

    get territoryLayerCollection(): TerritoryLayerCollection {
        if(!this._territoryLayerCollection) {
            throw new Error("Accessing TerritoryLayerCollection before it's initialised!");
        }
        return this._territoryLayerCollection;
    }

    destroy() {
        super.destroy();
        this._territoryLayerCollection?.getTerritoryLabelLayer()?.destroy();
        this._territoryLayerCollection = undefined;
    }
}
